import type {
  LightingAdvisorQuestion,
  LightingAnswers
} from '~/types/lightingAdvisor';
import { isQuestionVisible } from '~/utils/lightingAdvisor/questionDependencies';

export interface LightingSelectionSummaryItem {
  label: string;
  value: string;
  stepId: string;
}

function resolveChoiceLabel(
  questionId: string,
  value: string,
  questions: ReadonlyArray<LightingAdvisorQuestion>
): string {
  const question = questions.find((item) => item.id === questionId);
  const match = question?.inputs?.find((input) => input.value === value);
  return match?.text?.trim() || value;
}

function pushIfSet(
  items: LightingSelectionSummaryItem[],
  label: string,
  value: string | null | undefined,
  stepId: string
): void {
  const trimmed = value?.trim();
  if (label && trimmed) {
    items.push({ label, value: trimmed, stepId });
  }
}

export function buildLightingSelectionSummaryItems(options: {
  answers: LightingAnswers;
  questions: ReadonlyArray<LightingAdvisorQuestion>;
  labels: {
    workingDistance: string;
    objectSize: string;
    peakWavelength: string;
  };
}): LightingSelectionSummaryItem[] {
  const { answers, questions, labels } = options;
  const items: LightingSelectionSummaryItem[] = [];

  const questionLabel = (id: string, fallback: string): string =>
    questions.find((q) => q.id === id)?.label?.trim() || fallback;

  if (isQuestionVisible('a2s1q1', answers) && answers.a2s1q1) {
    pushIfSet(
      items,
      questionLabel('a2s1q1', 'Lighting technique'),
      resolveChoiceLabel('a2s1q1', String(answers.a2s1q1), questions),
      'step1'
    );
  }

  if (isQuestionVisible('a2s1q2', answers) && answers.a2s1q2) {
    pushIfSet(
      items,
      questionLabel('a2s1q2', 'Reflection'),
      resolveChoiceLabel('a2s1q2', String(answers.a2s1q2), questions),
      'step1'
    );
  }

  if (isQuestionVisible('a2s1q3', answers) && answers.a2s1q3) {
    pushIfSet(
      items,
      questionLabel('a2s1q3', 'Light type'),
      resolveChoiceLabel('a2s1q3', String(answers.a2s1q3), questions),
      'step1'
    );
  }

  if (answers.a2s2q1) {
    pushIfSet(
      items,
      questionLabel('a2s2q1', labels.workingDistance),
      `${answers.a2s2q1} mm`,
      'step2'
    );
  }

  const length = answers.a2s2q2_length;
  const width = answers.a2s2q2_width;
  if (length && width) {
    pushIfSet(items, labels.objectSize, `${length} × ${width} mm`, 'step2');
  }

  if (isQuestionVisible('a2s3q1', answers) && answers.a2s3q1) {
    pushIfSet(
      items,
      questionLabel('a2s3q1', 'Light color'),
      resolveChoiceLabel('a2s3q1', String(answers.a2s3q1), questions),
      'step3'
    );
  }

  if (isQuestionVisible('a2s3q2', answers) && answers.a2s3q2) {
    pushIfSet(
      items,
      questionLabel('a2s3q2', 'Feature'),
      resolveChoiceLabel('a2s3q2', String(answers.a2s3q2), questions),
      'step3'
    );
  }

  if (isQuestionVisible('a2s3q3', answers) && answers.a2s3q3) {
    pushIfSet(
      items,
      questionLabel('a2s3q3', labels.peakWavelength),
      resolveChoiceLabel('a2s3q3', String(answers.a2s3q3), questions),
      'step3'
    );
  }

  if (answers.a2s2q3) {
    pushIfSet(
      items,
      questionLabel('a2s2q3', 'Object moving'),
      resolveChoiceLabel('a2s2q3', String(answers.a2s2q3), questions),
      'step2'
    );
  }

  if (answers.a2s2q4) {
    pushIfSet(
      items,
      questionLabel('a2s2q4', 'Ambient light'),
      resolveChoiceLabel('a2s2q4', String(answers.a2s2q4), questions),
      'step2'
    );
  }

  if (answers.a2s4q0) {
    pushIfSet(
      items,
      questionLabel('a2s4q0', 'Number of lights'),
      String(answers.a2s4q0),
      'step4'
    );
  }

  if (answers.a2s4q1) {
    const functionLabels = String(answers.a2s4q1)
      .split(',')
      .map((value) => resolveChoiceLabel('a2s4q1', value.trim(), questions))
      .filter(Boolean);
    pushIfSet(
      items,
      questionLabel('a2s4q1', 'Light functions'),
      functionLabels.join(', '),
      'step4'
    );
  }

  if (answers.a2s5q1) {
    pushIfSet(
      items,
      questionLabel('a2s5q1', 'Application'),
      answers.a2s5q1 === 'further_application' && answers.a2s5q1_further
        ? String(answers.a2s5q1_further)
        : resolveChoiceLabel('a2s5q1', String(answers.a2s5q1), questions),
      'step5'
    );
  }

  if (answers.a2s5q2) {
    pushIfSet(
      items,
      questionLabel('a2s5q2', 'Camera'),
      resolveChoiceLabel('a2s5q2', String(answers.a2s5q2), questions),
      'step5'
    );
  }

  return items;
}
